// src/pages/ProductDetail.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import SEO from '../components/SEO';
import './ProductDetail.css';
import { useTranslation } from 'react-i18next';
import { pickDocField } from '../utils/i18nContent';

const DEFAULT_CATEGORIES = {
  tshirts: { fr: 'T-shirts', en: 'T-shirts', nl: 'T-shirts' },
  sweats: { fr: 'Sweats', en: 'Hoodies', nl: 'Truien' },
  pantalons: { fr: 'Pantalons', en: 'Pants', nl: 'Broeken' },
  shorts: { fr: 'Shorts', en: 'Shorts', nl: 'Shorts' },
  vestes: { fr: 'Vestes', en: 'Jackets', nl: 'Jassen' },
  accessoires: { fr: 'Accessoires', en: 'Accessories', nl: 'Accessoires' },
  goodies: { fr: 'Goodies', en: 'Goodies', nl: 'Goodies' }
};

const pickText = (value, lang = 'fr', fallback = 'fr') => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] || value[fallback] || Object.values(value).find(Boolean) || '';
};

const getCategoryLabel = (product, lang) => {
  if (product.categoryLabel_i18n) return pickText(product.categoryLabel_i18n, lang);
  if (product.categoryLabel) return product.categoryLabel;
  if (DEFAULT_CATEGORIES[product.category]) return pickText(DEFAULT_CATEGORIES[product.category], lang);
  return product.category || '';
};

function ProductDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation('shop');
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'products'),
          where('slug', '==', slug),
          where('status', '==', 'published')
        );
        const snap = await getDocs(q);

        if (!snap.empty) {
          const data = snap.docs[0].data();
          const productData = {
            id: snap.docs[0].id,
            ...data,
            price: Number(data.price) || 0,
            comparePrice: Number(data.comparePrice) || 0,
            inventory: Number(data.inventory) || 0
          };

          setProduct(productData);
          setSelectedSize(productData.sizes?.[0] || '');
          setSelectedColor(productData.colors?.[0] || '');
        } else {
          setProduct(null);
        }
      } catch (err) {
        console.error('Erreur récupération produit:', err);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  const addToCart = () => {
    if (!product) return;

    if (!selectedSize && product.sizes?.length > 0) {
      alert(t('selectSize'));
      return;
    }

    const cartItem = {
      productId: product.id,
      title: pickDocField(product, 'title', i18n.language, 'fr'),
      price: product.price,
      size: selectedSize,
      color: selectedColor,
      quantity,
      image: product.images?.[0]?.url
    };

    const existingCart = JSON.parse(localStorage.getItem('mkd-cart') || '[]');
    const existingItemIndex = existingCart.findIndex(item =>
      item.productId === cartItem.productId && item.size === cartItem.size && item.color === cartItem.color
    );

    if (existingItemIndex > -1) {
      existingCart[existingItemIndex].quantity += quantity;
    } else {
      existingCart.push(cartItem);
    }

    localStorage.setItem('mkd-cart', JSON.stringify(existingCart));
    alert(t('addedToCart'));
  };

  if (loading) {
    return (
      <div className="product-detail-container">
        <div className="container">
          <div className="loading">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-container">
        <div className="container">
          <div className="not-found">
            <h2>{t('productNotFound')}</h2>
          </div>
        </div>
      </div>
    );
  }

  const title = pickDocField(product, 'title', i18n.language, 'fr');
  const description = pickDocField(product, 'description', i18n.language, 'fr');
  const hasDiscount = product.comparePrice > product.price;
  const isOutOfStock = product.inventory === 0;
  const categoryLabel = getCategoryLabel(product, i18n.language);

  return (
    <div className="product-detail-container">
      <SEO title={title} description={description} image={product.images?.[0]?.url} />

      <div className="container">
        <div className="product-detail">
          <div className="product-gallery">
            <div className="main-image">
              <img src={product.images?.[selectedImage]?.url || '/placeholder-product.jpg'} alt={title} />
            </div>

            {product.images && product.images.length > 1 && (
              <div className="image-thumbnails">
                {product.images.map((image, index) => (
                  <button key={`${image.url}-${index}`} className={`thumbnail ${selectedImage === index ? 'active' : ''}`} onClick={() => setSelectedImage(index)}>
                    <img src={image.url} alt={`${title} ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="product-info">
            <h1>{title}</h1>

            <div className="product-price">
              <span className="current-price">{product.price.toFixed(2)}€</span>
              {hasDiscount && (
                <>
                  <span className="compare-price">{product.comparePrice.toFixed(2)}€</span>
                  <span className="discount-percent">-{Math.round((1 - product.price / product.comparePrice) * 100)}%</span>
                </>
              )}
            </div>

            <p className="product-description">{description}</p>

            {product.sizes && product.sizes.length > 0 && (
              <div className="size-selection">
                <label>{t('selectSize')}:</label>
                <div className="size-options">
                  {product.sizes.map(size => (
                    <button key={size} className={`size-option ${selectedSize === size ? 'selected' : ''}`} onClick={() => setSelectedSize(size)}>
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.colors && product.colors.length > 0 && (
              <div className="color-selection">
                <label>{t('selectColor')}:</label>
                <div className="color-options">
                  {product.colors.map(color => (
                    <button key={color} className={`color-option ${selectedColor === color ? 'selected' : ''}`} onClick={() => setSelectedColor(color)} title={color}>
                      <span style={{ backgroundColor: color.toLowerCase(), display: 'block', width: '20px', height: '20px', borderRadius: '50%', border: '1px solid #ddd' }} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="purchase-section">
              <div className="quantity-selector">
                <label>{t('quantity')}:</label>
                <div className="quantity-controls">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>-</button>
                  <span>{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} disabled={quantity >= product.inventory}>+</button>
                </div>
              </div>

              <button className="add-to-cart-btn" onClick={addToCart} disabled={isOutOfStock}>
                {isOutOfStock ? t('outOfStock') : t('addToCart')}
              </button>
            </div>

            <div className="stock-info">
              {product.inventory > 5 && <p className="in-stock">{t('inStock')}</p>}
              {product.inventory <= 5 && product.inventory > 0 && <p className="low-stock">{t('onlyLeft', { count: product.inventory })}</p>}
            </div>

            <div className="product-meta">
              {categoryLabel && <p><strong>{t('category')}:</strong> {categoryLabel}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
